/**
 * F-JP-23 — the decode pipeline, and the only place bytes become a record.
 *
 * The order of the steps is the contract, not an implementation detail:
 *
 *   bytes → schema header → reject future schemas → decode the type → validate
 *   → check that the decoded id equals the filename id
 *
 * Each step can only produce the failure that belongs to it, which is what
 * makes the recovery row honest. Parsing the body before reading the header
 * would report a record from a newer build as damaged content; checking
 * identity before validating would report a corrupt record as an identity
 * mismatch. The user reads that row and decides what to do, so a step out of
 * order is a wrong instruction, not a cosmetic slip.
 *
 * The one thing this module never does is repair. A record that fails anywhere
 * in the pipeline comes back as a failure carrying its reason and the identity
 * it *claims*; the bytes are untouched, and whether they may be copied into the
 * quarantine is a separate decision {@link journalRecordIsMovable} states
 * explicitly — unreadable bytes are never moved.
 */

import type {
  JournalDraft,
  JournalEntry,
  JournalGoal,
  JournalRecordType,
  JournalRecoveryReason,
} from "@ff/domain";
import { JOURNAL_SUPPORTED_SCHEMA_VERSIONS } from "@ff/domain";

import { canonicalJson } from "../kernel/canonicalJson";
import { journalUtf8Decode, journalUtf8Encode } from "./utf8";
import type { JournalValidation } from "./validation";
import {
  decodeJournalDraft,
  decodeJournalEntry,
  decodeJournalGoal,
} from "./validation";

/* ------------------------------------------------------------------------- *
 * Encoding
 * ------------------------------------------------------------------------- */

/**
 * A record's canonical bytes.
 *
 * Canonical rather than `JSON.stringify` because every write in this package is
 * verified by comparing the bytes that came back with the bytes that went in,
 * and insertion-order key output would make that comparison fail on values
 * nothing is wrong with.
 */
export function encodeJournalRecord(value: unknown): Uint8Array {
  return journalUtf8Encode(canonicalJson(value));
}

/* ------------------------------------------------------------------------- *
 * Decoding
 * ------------------------------------------------------------------------- */

export interface JournalRecordFailure {
  readonly reason: JournalRecoveryReason;
  /**
   * The identity the record claims: its header id, else the filename's, else
   * absent. It is what a recovery row is titled with, so it is deliberately the
   * record's *claim* rather than the location's — a mismatch between the two is
   * the failure being reported.
   */
  readonly identity: string | undefined;
  readonly detail: string;
}

export type JournalRecordDecode<T> =
  | { readonly kind: "decoded"; readonly value: T }
  | { readonly kind: "failed"; readonly failure: JournalRecordFailure };

/**
 * Whether a failed record may be copied into the quarantine.
 *
 * Only `unreadable` says no, and that single exception is the load-bearing one:
 * bytes this build could not even parse are bytes it cannot prove anything
 * about, and moving them would relocate the one copy of a record on the strength
 * of a guess. Everything else parsed far enough that its identity is known, so
 * the destination is known too.
 */
export function journalRecordIsMovable(reason: JournalRecoveryReason): boolean {
  return reason.kind !== "unreadable";
}

/** The header every record file starts with, read before the body is parsed. */
interface Header {
  readonly schemaVersion: number;
  readonly id: string | undefined;
}

function readHeader(raw: unknown): Header | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const version = source["schemaVersion"];
  if (typeof version !== "number" || !Number.isInteger(version)) return undefined;
  const id = source["id"];
  return { schemaVersion: version, id: typeof id === "string" ? id : undefined };
}

interface RecordDecoder<T> {
  readonly supported: number;
  readonly decode: (raw: unknown) => JournalValidation<T>;
  readonly identityOf: (value: T) => string;
}

const DECODERS = {
  entry: {
    supported: JOURNAL_SUPPORTED_SCHEMA_VERSIONS.entry,
    decode: (raw: unknown) => decodeJournalEntry(raw, "decode"),
    identityOf: (value: JournalEntry) => value.id as string,
  } satisfies RecordDecoder<JournalEntry>,
  draft: {
    supported: JOURNAL_SUPPORTED_SCHEMA_VERSIONS.draft,
    decode: (raw: unknown) => decodeJournalDraft(raw, "decode"),
    identityOf: (value: JournalDraft) => value.id as string,
  } satisfies RecordDecoder<JournalDraft>,
  goal: {
    supported: JOURNAL_SUPPORTED_SCHEMA_VERSIONS.goal,
    decode: decodeJournalGoal,
    identityOf: (value: JournalGoal) => value.id as string,
  } satisfies RecordDecoder<JournalGoal>,
} as const;

/** The decoded value a record type produces. */
export interface JournalRecordTypes {
  readonly entry: JournalEntry;
  readonly draft: JournalDraft;
  readonly goal: JournalGoal;
}

/**
 * Runs the pipeline over one file's bytes.
 *
 * `filenameId` is the identity the *location* asserts (F-JP-18: the filename IS
 * the identity). It is passed in rather than derived here because only the
 * caller knows whether these bytes came from a live path or from the
 * quarantine, and a quarantined file keeps the name it was preserved under.
 */
export function decodeJournalRecordBytes<K extends JournalRecordType>(
  type: K,
  bytes: Uint8Array,
  filenameId: string | undefined,
): JournalRecordDecode<JournalRecordTypes[K]> {
  const text = journalUtf8Decode(bytes);
  if (text === undefined) {
    return unreadable(filenameId, "the bytes are not valid UTF-8");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return unreadable(filenameId, "the bytes are not JSON");
  }

  const header = readHeader(parsed);
  if (header === undefined) {
    // No decodable header means no schema version, which means there is nothing
    // to decide *what shape* to read the body as. That is unreadable, not
    // invalid: a shape check needs a shape.
    return unreadable(filenameId, "no decodable schema header");
  }

  const decoder = DECODERS[type] as RecordDecoder<JournalRecordTypes[K]>;
  const identity = header.id ?? filenameId;

  if (header.schemaVersion > decoder.supported) {
    // Refused before the body is touched. A newer record read at this build's
    // version would lose the newer build's fields on the next save — a silent,
    // permanent loss disguised as a successful read.
    return {
      kind: "failed",
      failure: {
        reason: {
          kind: "futureSchema",
          found: header.schemaVersion,
          supported: decoder.supported,
        },
        identity,
        detail: `record is version ${header.schemaVersion}; this build reads ${decoder.supported}`,
      },
    };
  }

  const decoded = decoder.decode(parsed);
  if (!decoded.ok) {
    if ("futureSchema" in decoded) {
      const { found, supported } = decoded.futureSchema;
      return {
        kind: "failed",
        failure: {
          reason: { kind: "futureSchema", found, supported },
          identity,
          detail: `${decoded.issue.detail} at ${decoded.issue.path}`,
        },
      };
    }
    // Ids match → the content is what is wrong. Ids disagree → the identity is,
    // and a content complaint would send the user looking for damage in a
    // record whose real problem is where it is stored.
    const matches =
      header.id !== undefined &&
      filenameId !== undefined &&
      header.id.toLowerCase() === filenameId;
    return {
      kind: "failed",
      failure: {
        reason: { kind: matches ? "invalidContent" : "invalidIdentity" },
        identity,
        detail: `${decoded.issue.detail} at ${decoded.issue.path}`,
      },
    };
  }

  const decodedId = decoder.identityOf(decoded.value).toLowerCase();
  if (filenameId !== undefined && decodedId !== filenameId) {
    return {
      kind: "failed",
      failure: {
        reason: { kind: "invalidIdentity" },
        identity: decoder.identityOf(decoded.value),
        detail: `record claims ${decodedId} but is stored as ${filenameId}`,
      },
    };
  }

  return { kind: "decoded", value: decoded.value };
}

function unreadable<T>(
  filenameId: string | undefined,
  detail: string,
): JournalRecordDecode<T> {
  return {
    kind: "failed",
    failure: { reason: { kind: "unreadable" }, identity: filenameId, detail },
  };
}
